import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { PLACEMENT_QUESTIONS, PlacementTestService } from '../../services/PlacementTestService';

export default function PlacementTestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : 'guest';

  const [step, setStep] = useState(0); // question index, or PLACEMENT_QUESTIONS.length for result
  const [answers, setAnswers] = useState([]);
  const [placedIndex, setPlacedIndex] = useState(null);

  const question = PLACEMENT_QUESTIONS[step];
  const done = step >= PLACEMENT_QUESTIONS.length;

  function choose(score) {
    const next = [...answers, score];
    setAnswers(next);
    if (step + 1 >= PLACEMENT_QUESTIONS.length) {
      const total = next.reduce((sum, s) => sum + s, 0);
      const index = PlacementTestService.applyResult(langPair, total);
      setPlacedIndex(index);
    }
    setStep(step + 1);
  }

  function goBack() {
    if (step === 0) {
      navigate(-1);
      return;
    }
    setAnswers((prev) => prev.slice(0, -1));
    setStep((prev) => prev - 1);
  }

  if (done) {
    const stepLabel = config.learningSteps[placedIndex]?.label
      || config.learningSteps[0]?.label
      || '';
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
        <div className="mx-auto max-w-md space-y-5 text-center">
          <span className="text-5xl">🎯</span>
          <h1 className="text-2xl font-black text-slate-900">{t('placement.resultTitle')}</h1>
          <p className="text-sm text-slate-600">
            {t('placement.resultDesc', { step: stepLabel })}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mx-auto mt-2 w-full max-w-xs rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-3 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5"
          >
            {t('placement.startLearning')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-md space-y-5">
        <button
          onClick={goBack}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {step === 0 ? t('common.cancel') : t('common.back')}
        </button>

        <div className="flex gap-1.5">
          {PLACEMENT_QUESTIONS.map((q, i) => (
            <span
              key={q.id}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-indigo-500' : 'bg-indigo-100'}`}
            />
          ))}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-widest text-indigo-500">
            {t('placement.questionOf', { current: step + 1, total: PLACEMENT_QUESTIONS.length })}
          </p>
          <h2 className="mt-2 text-lg font-black text-slate-900">{t(question.labelKey)}</h2>
          <div className="mt-4 space-y-2">
            {question.optionKeys.map((key, score) => (
              <button
                key={key}
                onClick={() => choose(score)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:border-indigo-400 hover:bg-indigo-50"
              >
                {t(key)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
